import type { AreaComponent, Placement, Surface } from '@repo/api-contracts';
import type { PointerEvent } from 'react';
import { isPlacementAllowed, type PhotoDimensions, shapeRingsOnPhoto } from '@/entities/edit-area';
import { componentSizeLabel } from '@/shared/lib';
import { closedPathOf } from '../model/svg-path';
import { ComponentGridOverlay } from './component-grid-overlay';

interface Props {
  component: AreaComponent;
  surface: Surface;
  placement: Placement;
  photoSize: PhotoDimensions;
  disabled: boolean;
  gridSpacing: number | null;
  /** 사진 위에서 고른 영역 — 테두리를 진하게 그린다. */
  selected?: boolean;
  onPointerStart: (event: PointerEvent<SVGElement>) => void;
}

/**
 * 배치 경계를 인쇄 영역 모양 그대로(shapeRingsOnPhoto) evenodd path 하나로 그린다 — 구멍은 비고, 원통에서 둘레로 감긴
 * 조각(최대 둘, 결정 Q6)도 같은 path 의 하위 경로다. 보이는 고리가 없거나 mesh 면 그리지 않는다.
 * 이름표는 첫 고리의 첫 점에 놓는다.
 */
export function PlacementOverlay({
  component,
  surface,
  placement,
  photoSize,
  disabled,
  gridSpacing,
  selected = false,
  onPointerStart,
}: Props) {
  const rings = shapeRingsOnPhoto(surface, photoSize, component, placement.position);
  const anchor = rings?.[0]?.[0];
  if (rings === null || anchor === undefined) return null;
  const valid = isPlacementAllowed(component, surface, placement.position);
  const paint = {
    fill: valid ? '#10b98120' : '#ef444430',
    stroke: valid ? '#059669' : '#dc2626',
    strokeWidth: selected ? 4 : 2,
    vectorEffect: 'non-scaling-stroke',
  } as const;
  const grab = {
    onPointerDown: onPointerStart,
    style: { cursor: disabled ? 'default' : 'move' },
    'data-testid': `placement-${component.id}`,
    'data-selected': selected ? 'true' : 'false',
  };
  const title = (
    <title>
      {component.name}: {componentSizeLabel(component)}
      {valid ? '' : ' — 표면 범위나 제품 윤곽을 벗어났습니다'}
    </title>
  );
  return (
    <g>
      <path d={rings.map(closedPathOf).join(' ')} fillRule="evenodd" {...paint} {...grab}>
        {title}
      </path>
      {gridSpacing !== null && (
        <ComponentGridOverlay
          component={component}
          surface={surface}
          placement={placement}
          photoSize={photoSize}
          spacingMm={gridSpacing}
        />
      )}
      <text
        x={anchor.x + 4}
        y={anchor.y + Math.max(14, photoSize.widthPx * 0.012)}
        fontSize={Math.max(12, photoSize.widthPx * 0.012)}
        fill="#064e3b"
        pointerEvents="none"
      >
        {component.name}
      </text>
    </g>
  );
}
